import {
  IntegrationProvider,
  isRecordingS3IntakeProvider,
} from "@repo/app-types/entities/integration";
import {
  recordingImportGenesysMetadataSchema,
  recordingImportNiceCxoneMetadataSchema,
  recordingImportS3MetadataSchema,
  recordingImportZoomContactCenterMetadataSchema,
  type RecordingImportGenesysMetadata,
  type RecordingImportNiceCxoneMetadata,
  type RecordingImportRow,
  type RecordingImportZoomContactCenterMetadata,
} from "@repo/app-types/entities/recordingImport";
import type { EntityTableColumnDefinition } from "@repo/ui/tables/entityTable";
import { DateTime } from "luxon";

/**
 * What the generic Failed imports tab shows of a row's provider-shaped `metadata`:
 * the columns for it, and how those columns name a row to a screen reader.
 * One entry per provider family; a provider without one falls back to the
 * source's `external_id`, which every provider has.
 */
export interface RecordingImportProviderColumns {
  /** Columns inserted after "Received at", before the status-specific ones. */
  columns: readonly EntityTableColumnDefinition<RecordingImportRow>[];
  /** A short, unique description of the row (for the selection checkbox label). */
  describeRow: (row: RecordingImportRow) => string;
}

/**
 * Plain, selectable text — deliberately no button in the cell. A focusable
 * child makes react-aria's table treat the whole cell as interactive and
 * swallow pointer-down, which blocks drag-selecting the value. Wrapping keeps
 * it fully visible and find-in-page-matchable, and `whitespace-pre-wrap`
 * preserves the exact spacing so a pasted value matches a search.
 */
function renderIdentifierCell(value: string) {
  return (
    <span className="block max-w-[22rem] break-all whitespace-pre-wrap text-foreground">
      {value}
    </span>
  );
}

/** The S3 object key: the customer's own file name, as they uploaded it. */
function readS3ObjectKey(row: RecordingImportRow): string {
  const parsed = recordingImportS3MetadataSchema.safeParse(row.metadata);
  return parsed.success ? parsed.data.object_key : row.externalId;
}

const s3Columns: RecordingImportProviderColumns = {
  columns: [
    {
      id: "object_key",
      header: "File",
      getTextValue: readS3ObjectKey,
      renderCell: (row) => renderIdentifierCell(readS3ObjectKey(row)),
    },
  ],
  describeRow: readS3ObjectKey,
};

/**
 * A Zoom Contact Center row's engagement (the call, one recording per agent
 * leg) and the recording within it, from `metadata`. Ids only: the agent,
 * customer and times are read from Zoom at import, and the error code says why
 * a row didn't import. Empty strings for a row whose metadata is not the Zoom
 * shape, which the drain settles as `invalid_metadata`.
 */
function readZoomLocator(
  row: RecordingImportRow,
): RecordingImportZoomContactCenterMetadata {
  const parsed = recordingImportZoomContactCenterMetadataSchema.safeParse(
    row.metadata,
  );
  return parsed.success ? parsed.data : { engagement_id: "", recording_id: "" };
}

const zoomContactCenterColumns: RecordingImportProviderColumns = {
  columns: [
    {
      id: "engagement_id",
      header: "Engagement ID",
      getTextValue: (row) => readZoomLocator(row).engagement_id,
      renderCell: (row) =>
        renderIdentifierCell(readZoomLocator(row).engagement_id),
    },
    {
      id: "recording_id",
      header: "Recording ID",
      getTextValue: (row) => readZoomLocator(row).recording_id,
      renderCell: (row) =>
        renderIdentifierCell(readZoomLocator(row).recording_id),
    },
  ],
  describeRow: (row) => {
    const { engagement_id: engagementId, recording_id: recordingId } =
      readZoomLocator(row);
    return `Engagement ${engagementId}, recording ${recordingId}`;
  },
};

/**
 * A Genesys row's conversation (the call, what the customer's Genesys admin
 * searches on) and the recording within it, from `metadata`; `external_id` is
 * only the ledger's uniqueness key. A transferred call recorded per agent leg
 * has one row per recording under the same conversation. Empty strings for a
 * row whose metadata is not the Genesys shape, which the drain settles as
 * `invalid_metadata`.
 */
function readGenesysLocator(
  row: RecordingImportRow,
): RecordingImportGenesysMetadata {
  const parsed = recordingImportGenesysMetadataSchema.safeParse(row.metadata);
  return parsed.success
    ? parsed.data
    : { conversation_id: "", recording_id: "" };
}

const genesysColumns: RecordingImportProviderColumns = {
  columns: [
    {
      id: "conversation_id",
      header: "Conversation ID",
      getTextValue: (row) => readGenesysLocator(row).conversation_id,
      renderCell: (row) =>
        renderIdentifierCell(readGenesysLocator(row).conversation_id),
    },
    {
      id: "recording_id",
      header: "Recording ID",
      getTextValue: (row) => readGenesysLocator(row).recording_id,
      renderCell: (row) =>
        renderIdentifierCell(readGenesysLocator(row).recording_id),
    },
  ],
  describeRow: (row) => {
    const { conversation_id: conversationId, recording_id: recordingId } =
      readGenesysLocator(row);
    return `Conversation ${conversationId}, recording ${recordingId}`;
  },
};

/**
 * A NICE CXone row's call, as NICE exported it: the agent, when the call
 * started, its direction, and the segment (NICE's id for the recording).
 * Empty values for a row whose metadata is not the NICE shape, which the drain
 * settles as `invalid_metadata`.
 */
function readNiceCxoneSegment(
  row: RecordingImportRow,
): RecordingImportNiceCxoneMetadata {
  const parsed = recordingImportNiceCxoneMetadataSchema.safeParse(row.metadata);
  return parsed.success
    ? parsed.data
    : {
        object_key: "",
        segment_id: row.externalId,
        agent_name: "",
        started_at: "",
        direction: "",
        ani: "",
        dnis: "",
      };
}

function formatNiceCxoneCallStart(row: RecordingImportRow): string {
  const startedAt = DateTime.fromISO(readNiceCxoneSegment(row).started_at);
  return startedAt.isValid
    ? startedAt.toLocaleString(DateTime.DATETIME_MED)
    : "";
}

const niceCxoneColumns: RecordingImportProviderColumns = {
  columns: [
    {
      id: "agent_name",
      header: "Agent",
      getTextValue: (row) => readNiceCxoneSegment(row).agent_name,
      renderCell: (row) =>
        renderIdentifierCell(readNiceCxoneSegment(row).agent_name),
    },
    {
      id: "started_at",
      header: "Call started",
      getTextValue: formatNiceCxoneCallStart,
      renderCell: formatNiceCxoneCallStart,
    },
    {
      id: "direction",
      header: "Direction",
      getTextValue: (row) => readNiceCxoneSegment(row).direction,
      renderCell: (row) => readNiceCxoneSegment(row).direction,
    },
    {
      id: "segment_id",
      header: "Segment ID",
      getTextValue: (row) => readNiceCxoneSegment(row).segment_id,
      renderCell: (row) =>
        renderIdentifierCell(readNiceCxoneSegment(row).segment_id),
    },
  ],
  describeRow: (row) => `Segment ${readNiceCxoneSegment(row).segment_id}`,
};

const defaultColumns: RecordingImportProviderColumns = {
  columns: [
    {
      id: "external_id",
      header: "Recording",
      getTextValue: (row) => row.externalId,
      renderCell: (row) => renderIdentifierCell(row.externalId),
    },
  ],
  describeRow: (row) => row.externalId,
};

export function resolveRecordingImportProviderColumns(
  provider: IntegrationProvider,
): RecordingImportProviderColumns {
  if (isRecordingS3IntakeProvider(provider)) {
    return s3Columns;
  }
  if (provider === IntegrationProvider.GENESYS) {
    return genesysColumns;
  }
  if (provider === IntegrationProvider.ZOOM_CONTACT_CENTER) {
    return zoomContactCenterColumns;
  }
  if (provider === IntegrationProvider.NICE_CXONE) {
    return niceCxoneColumns;
  }
  return defaultColumns;
}
